import React, { useState, useEffect, useCallback } from 'react';
import { Button, Input, LoadingSpinner, Modal } from './';

/**
 * MimePanel - Host control panel for MIME (Make It More Epic) rounds
 * Used on the EventOperationPage to create and manage MIME rounds
 */
export default function MimePanel({
  eventId,
  mimeService,
  acceptedCount = 0,
  eventStatus = 'draft',
}) {
  const [summary, setSummary] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [activeRound, setActiveRound] = useState(null);
  const [secondsRemaining, setSecondsRemaining] = useState(0);
  const [requests, setRequests] = useState([]);

  // Create round modal state
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createForm, setCreateForm] = useState({
    additionalCommitmentCents: 500, // P$5.00 default
    message: '',
    shotClockMinutes: 60,
    removeNonResponders: false,
  });
  const [isCreating, setIsCreating] = useState(false);
  const [createError, setCreateError] = useState(null);

  // Fetch MIME summary
  const fetchSummary = useCallback(async () => {
    if (!eventId || !mimeService) return;

    try {
      const result = await mimeService.getEventSummary(eventId);
      if (result.success) {
        setSummary(result.data);
        if (result.data?.activeRound) {
          setActiveRound(result.data.activeRound);
          setSecondsRemaining(result.data.activeRound.secondsRemaining || 0);

          // Fetch requests for active round
          const requestsResult = await mimeService.getRoundRequests(result.data.activeRound.id);
          if (requestsResult.success) {
            setRequests(requestsResult.data || []);
          }
        } else {
          setActiveRound(null);
          setRequests([]);
        }
      }
    } catch (err) {
      console.error('Failed to fetch MIME summary:', err);
    } finally {
      setIsLoading(false);
    }
  }, [eventId, mimeService]);

  // Initial load
  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  // Poll for updates when there's an active round
  useEffect(() => {
    if (!activeRound) return;

    const pollInterval = setInterval(() => {
      fetchSummary();
    }, 5000); // Poll every 5 seconds

    return () => clearInterval(pollInterval);
  }, [activeRound, fetchSummary]);

  // Countdown timer
  useEffect(() => {
    if (!activeRound || secondsRemaining <= 0) return;

    const timer = setInterval(() => {
      setSecondsRemaining((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          fetchSummary(); // Refresh when timer expires
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [activeRound, secondsRemaining, fetchSummary]);

  // Format time as HH:MM:SS or MM:SS
  const formatTime = (seconds) => {
    if (seconds <= 0) return '00:00';

    const hours = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;

    if (hours > 0) {
      return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Format P$ amount
  const formatAmount = (cents) => {
    return `P$${(cents / 100).toFixed(2)}`;
  };

  // Handle create round
  const handleCreateRound = async () => {
    setIsCreating(true);
    setCreateError(null);

    try {
      const result = await mimeService.createRound(eventId, createForm);
      if (result.success) {
        setShowCreateModal(false);
        setCreateForm({
          additionalCommitmentCents: 500,
          message: '',
          shotClockMinutes: 60,
          removeNonResponders: false,
        });
        fetchSummary();
      } else {
        setCreateError(result.error || 'Failed to create MIME round');
      }
    } catch (err) {
      setCreateError('An unexpected error occurred');
    } finally {
      setIsCreating(false);
    }
  };

  // Handle cancel round
  const handleCancelRound = async () => {
    if (!activeRound) return;

    if (!window.confirm('Are you sure you want to cancel this MIME round? Pending requests will be expired.')) {
      return;
    }

    try {
      const result = await mimeService.cancelRound(activeRound.id);
      if (result.success) {
        fetchSummary();
      } else {
        alert(result.error || 'Failed to cancel MIME round');
      }
    } catch (err) {
      alert('An unexpected error occurred');
    }
  };

  // Can create new round?
  const canCreateRound = !activeRound &&
    acceptedCount > 0 &&
    (eventStatus === 'pledging' || eventStatus === 'greenlit' || eventStatus === 'published');

  if (isLoading) {
    return (
      <div className="border-2 border-black p-6">
        <LoadingSpinner label="Loading MIME..." />
      </div>
    );
  }

  return (
    <div className="border-2 border-black">
      {/* Header */}
      <div className="px-6 py-4 border-b-2 border-black bg-gradient-to-r from-purple-50 to-pink-50">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="px-3 py-1 bg-black text-white font-mono text-xs font-bold uppercase tracking-wider">
              MIME
            </span>
            <h2 className="font-mono text-sm font-bold uppercase tracking-wider text-black">
              MAKE IT MORE EPIC
            </h2>
          </div>
          {summary && (
            <span className="font-mono text-xs text-gray-600">
              {summary.totalRounds} round{summary.totalRounds !== 1 ? 's' : ''} total
            </span>
          )}
        </div>
      </div>

      {/* Active Round */}
      {activeRound && (
        <div className="px-6 py-4 border-b-2 border-black bg-yellow-50">
          <div className="flex items-start justify-between gap-4 mb-4">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <span className="px-2 py-0.5 bg-yellow-500 text-black font-mono text-xs font-bold uppercase tracking-wider animate-pulse">
                  ACTIVE
                </span>
                <span className="font-mono text-sm font-bold text-black">
                  Round #{activeRound.roundNumber}
                </span>
              </div>
              <p className="font-mono text-sm text-gray-700">
                {formatAmount(activeRound.additionalCommitmentCents)} additional commitment
              </p>
            </div>

            {/* Shot Clock */}
            <div className="text-right">
              <div className={`font-mono text-3xl font-bold tabular-nums ${
                secondsRemaining < 300 ? 'text-red-600' :
                secondsRemaining < 900 ? 'text-yellow-600' : 'text-black'
              }`}>
                {formatTime(secondsRemaining)}
              </div>
              <p className="font-mono text-[10px] uppercase tracking-wider text-gray-500">
                TIME REMAINING
              </p>
            </div>
          </div>

          {/* Response Stats */}
          <div className="grid grid-cols-4 gap-3 mb-4">
            <div className="text-center p-2 bg-white border border-gray-200">
              <p className="font-mono text-xl font-bold text-black">{activeRound.totalRequests}</p>
              <p className="font-mono text-[10px] uppercase tracking-wider text-gray-500">TOTAL</p>
            </div>
            <div className="text-center p-2 bg-green-100 border border-green-300">
              <p className="font-mono text-xl font-bold text-green-700">{activeRound.acceptedCount}</p>
              <p className="font-mono text-[10px] uppercase tracking-wider text-green-600">ACCEPTED</p>
            </div>
            <div className="text-center p-2 bg-red-100 border border-red-300">
              <p className="font-mono text-xl font-bold text-red-700">{activeRound.declinedCount}</p>
              <p className="font-mono text-[10px] uppercase tracking-wider text-red-600">DECLINED</p>
            </div>
            <div className="text-center p-2 bg-gray-100 border border-gray-300">
              <p className="font-mono text-xl font-bold text-gray-700">{activeRound.pendingCount}</p>
              <p className="font-mono text-[10px] uppercase tracking-wider text-gray-500">PENDING</p>
            </div>
          </div>

          {/* Individual Responses */}
          {requests.length > 0 && (
            <div className="mb-4">
              <p className="font-mono text-xs font-bold uppercase tracking-wider text-gray-600 mb-2">
                RESPONSES
              </p>
              <div className="space-y-1 max-h-32 overflow-y-auto">
                {requests.map((req) => (
                  <div
                    key={req.id}
                    className={`flex items-center justify-between px-3 py-2 text-sm ${
                      req.status === 'Accepted' ? 'bg-green-50' :
                      req.status === 'Declined' ? 'bg-red-50' :
                      'bg-white'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      {req.guest?.avatarUrl ? (
                        <img
                          src={req.guest.avatarUrl}
                          alt={req.guest.displayName}
                          className="w-6 h-6 border border-black object-cover"
                        />
                      ) : (
                        <div className="w-6 h-6 bg-gray-200 border border-black flex items-center justify-center">
                          <span className="font-mono text-xs text-gray-400">?</span>
                        </div>
                      )}
                      <span className="font-mono text-xs font-bold uppercase tracking-wider">
                        {req.guest?.displayName || 'Guest'}
                      </span>
                    </div>
                    <span className={`font-mono text-xs font-bold uppercase tracking-wider ${
                      req.status === 'Accepted' ? 'text-green-600' :
                      req.status === 'Declined' ? 'text-red-600' :
                      req.status === 'Expired' ? 'text-gray-400' :
                      'text-yellow-600'
                    }`}>
                      {req.status}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Cancel Button */}
          <Button
            variant="secondary"
            size="sm"
            onClick={handleCancelRound}
          >
            CANCEL ROUND
          </Button>
        </div>
      )}

      {/* Create New Round */}
      <div className="px-6 py-4">
        {canCreateRound ? (
          <Button
            variant="primary"
            fullWidth
            onClick={() => setShowCreateModal(true)}
          >
            CREATE MIME ROUND
          </Button>
        ) : !activeRound && acceptedCount === 0 ? (
          <p className="font-mono text-sm text-gray-500 text-center">
            Accept guests first to send MIME requests
          </p>
        ) : !activeRound && eventStatus === 'draft' ? (
          <p className="font-mono text-sm text-gray-500 text-center">
            Publish event to create MIME rounds
          </p>
        ) : activeRound ? (
          <p className="font-mono text-sm text-gray-500 text-center">
            Complete or cancel the active round to create a new one
          </p>
        ) : null}

        {/* Completed Rounds Summary */}
        {summary && summary.completedRounds.length > 0 && (
          <div className="mt-4 pt-4 border-t border-gray-200">
            <p className="font-mono text-xs font-bold uppercase tracking-wider text-gray-600 mb-2">
              COMPLETED ROUNDS
            </p>
            <div className="space-y-2">
              {summary.completedRounds.slice(0, 3).map((round) => (
                <div key={round.id} className="flex items-center justify-between text-sm">
                  <span className="font-mono text-xs text-gray-600">
                    Round #{round.roundNumber}
                  </span>
                  <span className="font-mono text-xs font-bold text-black">
                    {round.acceptedCount}/{round.totalRequests} accepted
                  </span>
                </div>
              ))}
            </div>
            {summary.totalAdditionalCommitmentsCents > 0 && (
              <div className="mt-3 pt-3 border-t border-gray-200">
                <div className="flex items-center justify-between">
                  <span className="font-mono text-xs text-gray-600 uppercase tracking-wider">
                    Total Additional Commitments
                  </span>
                  <span className="font-mono text-sm font-bold text-green-600">
                    {formatAmount(summary.totalAdditionalCommitmentsCents)}
                  </span>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Create MIME Round Modal */}
      <Modal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        title="Create MIME Round"
      >
        <div className="space-y-4">
          <p className="font-mono text-sm text-gray-600">
            Request additional P$ commitment from accepted guests.
            They'll have limited time to respond.
          </p>

          {/* Amount */}
          <div>
            <label className="font-mono text-xs font-bold uppercase tracking-wider text-gray-600 mb-1 block">
              Additional Commitment (P$)
            </label>
            <Input
              type="number"
              value={createForm.additionalCommitmentCents / 100}
              onChange={(e) => setCreateForm({
                ...createForm,
                additionalCommitmentCents: Math.round(parseFloat(e.target.value || 0) * 100)
              })}
              min={1}
              max={1000}
              step={0.5}
            />
            <p className="font-mono text-xs text-gray-500 mt-1">
              Between P$1.00 and P$1000.00
            </p>
          </div>

          {/* Message */}
          <div>
            <label className="font-mono text-xs font-bold uppercase tracking-wider text-gray-600 mb-1 block">
              Message to Guests (Optional)
            </label>
            <textarea
              className="w-full px-4 py-3 border-2 border-black font-mono text-sm focus:outline-none focus:ring-2 focus:ring-black"
              rows={3}
              value={createForm.message}
              onChange={(e) => setCreateForm({ ...createForm, message: e.target.value })}
              placeholder="Why should guests commit more?"
              maxLength={500}
            />
          </div>

          {/* Shot Clock */}
          <div>
            <label className="font-mono text-xs font-bold uppercase tracking-wider text-gray-600 mb-1 block">
              Response Time (Minutes)
            </label>
            <select
              className="w-full px-4 py-3 border-2 border-black font-mono text-sm focus:outline-none focus:ring-2 focus:ring-black bg-white"
              value={createForm.shotClockMinutes}
              onChange={(e) => setCreateForm({ ...createForm, shotClockMinutes: parseInt(e.target.value) })}
            >
              <option value={15}>15 minutes</option>
              <option value={30}>30 minutes</option>
              <option value={60}>1 hour</option>
              <option value={120}>2 hours</option>
              <option value={360}>6 hours</option>
              <option value={720}>12 hours</option>
              <option value={1440}>24 hours</option>
            </select>
          </div>

          {/* Remove Non-Responders */}
          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              id="removeNonResponders"
              checked={createForm.removeNonResponders}
              onChange={(e) => setCreateForm({ ...createForm, removeNonResponders: e.target.checked })}
              className="w-5 h-5 border-2 border-black"
            />
            <label htmlFor="removeNonResponders" className="font-mono text-sm text-gray-700">
              Remove guests who don't respond
            </label>
          </div>

          {createError && (
            <p className="font-mono text-xs text-red-600">{createError}</p>
          )}

          <div className="flex gap-3 pt-4">
            <Button
              variant="secondary"
              onClick={() => setShowCreateModal(false)}
              disabled={isCreating}
            >
              CANCEL
            </Button>
            <Button
              variant="primary"
              onClick={handleCreateRound}
              disabled={isCreating || createForm.additionalCommitmentCents < 100}
              className="flex-1"
            >
              {isCreating ? 'CREATING...' : 'SEND MIME REQUESTS'}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
